import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const colors = {
  primary: '#4F46E5',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  cardBackground: '#FFFFFF',
  border: '#E2E8F0',
  text: '#1E293B',
  textSecondary: '#64748B',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

const HomeLoanEMICalculatorScreen = () => {
  const [loanAmount, setLoanAmount] = useState('1000000');
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTenure, setLoanTenure] = useState(5);
  const [tenureType, setTenureType] = useState('years');
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);
  const [monthlyIncome, setMonthlyIncome] = useState('50000');
  const [showAffordability, setShowAffordability] = useState(false);

  const tenureOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
  ];

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    if (!num) return '';
    const cleanNum = num.toString().replace(/[^\d]/g, '');
    return cleanNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to clean formatted numbers
  const cleanNumber = (num) => {
    return num.toString().replace(/[^\d]/g, '');
  };

  // Handle loan amount change with formatting
  const handleLoanAmountChange = (value) => {
    const cleanValue = cleanNumber(value);
    setLoanAmount(cleanValue);
  };

  // Handle monthly income change with formatting
  const handleMonthlyIncomeChange = (value) => {
    const cleanValue = cleanNumber(value);
    setMonthlyIncome(cleanValue);
  };

  const calculateEMI = () => {
    const principal = parseFloat(cleanNumber(loanAmount));
    const rate = parseFloat(interestRate);
    const tenure = parseFloat(loanTenure);

    // Input validation
    if (isNaN(principal) || isNaN(rate) || isNaN(tenure)) {
      return;
    }

    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      return;
    }

    if (rate > 50) {
      return;
    }

    const tenureInMonths = tenureType === 'years' ? tenure * 12 : tenure;
    
    if (tenureInMonths > 360) {
      return;
    }

    const monthlyRate = rate / 100 / 12;
    
    // EMI calculation using formula: P * r * (1+r)^n / ((1+r)^n - 1)
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureInMonths)) / 
                (Math.pow(1 + monthlyRate, tenureInMonths) - 1);
    
    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    // Generate year-wise breakdown for amortization schedule
    const breakdown = [];
    let remainingPrincipal = principal;
    
    for (let year = 1; year <= Math.ceil(tenureInMonths / 12); year++) {
      const startMonth = (year - 1) * 12 + 1;
      const endMonth = Math.min(year * 12, tenureInMonths);
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthlyInterest = remainingPrincipal * monthlyRate;
        const monthlyPrincipal = emi - monthlyInterest;
        
        yearlyInterest += monthlyInterest;
        yearlyPrincipal += monthlyPrincipal;
        remainingPrincipal -= monthlyPrincipal;
      }
      
      breakdown.push({
        year,
        principal: Math.round(yearlyPrincipal),
        interest: Math.round(yearlyInterest),
        balance: Math.max(0, Math.round(remainingPrincipal)),
      });
    }

    setResults({
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      principal: Math.round(principal),
      breakdown,
      tenureInMonths,
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTenure, tenureType]);

  const loadMoreRows = () => {
    setDisplayedRows(prev => prev + 5);
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>Home Loan EMI Calculator</Text>
        <Text style={styles.headerSubtitle}>
          Calculate your home loan EMI, total interest, and payment schedule
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <Text style={styles.sectionTitle}>Loan Details</Text>
      
      {/* Loan Amount Input */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Loan amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={formatNumber(loanAmount)}
            onChangeText={handleLoanAmountChange}
            placeholder="10,00,000"
            keyboardType="numeric"
            placeholderTextColor={colors.textSecondary}
          />
        </View>
      </View>

      {/* Preset Loan Amount Buttons */}
      <View style={styles.presetContainer}>
        <Text style={styles.presetLabel}>Quick Select:</Text>
        <View style={styles.presetButtons}>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('500000')}
          >
            <Text style={styles.presetButtonText}>₹5L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('1000000')}
          >
            <Text style={styles.presetButtonText}>₹10L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('2500000')}
          >
            <Text style={styles.presetButtonText}>₹25L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('5000000')}
          >
            <Text style={styles.presetButtonText}>₹50L</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Interest Rate Slider */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Rate of interest (p.a)</Text>
          <View style={styles.valueContainer}>
            <Text style={styles.valueText}>{interestRate.toFixed(1)}%</Text>
          </View>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={1}
          maximumValue={20}
          value={interestRate}
          onValueChange={setInterestRate}
          minimumTrackTintColor={colors.success}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
      </View>

      {/* Loan Tenure Slider */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Loan tenure</Text>
          <View style={styles.valueContainer}>
            <Text style={styles.valueText}>{loanTenure} {tenureType === 'years' ? 'Yr' : 'Mo'}</Text>
          </View>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={tenureType === 'years' ? 1 : 12}
          maximumValue={tenureType === 'years' ? 30 : 360}
          value={loanTenure}
          onValueChange={setLoanTenure}
          minimumTrackTintColor={colors.success}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
      </View>

      {/* Loan Affordability Check */}
      <View style={styles.affordabilityContainer}>
        <TouchableOpacity 
          style={styles.affordabilityHeader}
          onPress={() => setShowAffordability(!showAffordability)}
        >
          <Text style={styles.affordabilityTitle}>Loan Affordability Check</Text>
          <Ionicons 
            name={showAffordability ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.textSecondary} 
          />
        </TouchableOpacity>
        
        {showAffordability && (
          <View style={styles.affordabilityContent}>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Monthly Income</Text>
              <View style={styles.inputContainer}>
                <Text style={styles.currencySymbol}>₹</Text>
                <TextInput
                  style={styles.textInput}
                  value={formatNumber(monthlyIncome)}
                  onChangeText={handleMonthlyIncomeChange}
                  placeholder="50,000"
                  keyboardType="numeric"
                  placeholderTextColor={colors.textSecondary}
                />
              </View>
            </View>
            
            {results && monthlyIncome && (
              <View style={styles.affordabilityResults}>
                <View style={styles.affordabilityItem}>
                  <Text style={styles.affordabilityLabel}>EMI to Income Ratio:</Text>
                  <Text style={[
                    styles.affordabilityValue,
                    { color: (results.emi / parseFloat(cleanNumber(monthlyIncome))) * 100 <= 40 ? colors.success : colors.warning }
                  ]}>
                    {((results.emi / parseFloat(cleanNumber(monthlyIncome))) * 100).toFixed(1)}%
                  </Text>
                </View>
                <Text style={styles.affordabilityNote}>
                  {(results.emi / parseFloat(cleanNumber(monthlyIncome))) * 100 <= 40 
                    ? "✓ This loan is affordable (≤40% of income)" 
                    : "⚠ This loan may strain your budget (>40% of income)"}
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );



  const renderAmortizationSchedule = () => {
    if (!results) return null;

    return (
      <View style={styles.scheduleContainer}>
        <Text style={styles.scheduleTitle}>Yearly repayment schedule</Text>
        <View style={styles.scheduleTable}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableHeaderText}>Year</Text>
            <Text style={styles.tableHeaderText}>Beginning Balance</Text>
            <Text style={styles.tableHeaderText}>EMI</Text>
            <Text style={styles.tableHeaderText}>Principal</Text>
            <Text style={styles.tableHeaderText}>Interest</Text>
            <Text style={styles.tableHeaderText}>Ending Balance</Text>
          </View>
          {results.breakdown.slice(0, displayedRows).map((row, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCellText}>{row.year}</Text>
              <Text style={styles.tableCellText}>
                ₹{formatNumber(index === 0 ? results.principal : results.breakdown[index - 1].balance)}
              </Text>
              <Text style={styles.tableCellText}>₹{formatNumber(results.emi * 12)}</Text>
              <Text style={styles.tableCellText}>₹{formatNumber(row.principal)}</Text>
              <Text style={styles.tableCellText}>₹{formatNumber(row.interest)}</Text>
              <Text style={styles.tableCellText}>₹{formatNumber(row.balance)}</Text>
            </View>
          ))}
        </View>
        {displayedRows < results.breakdown.length && (
          <TouchableOpacity style={styles.loadMoreButton} onPress={loadMoreRows}>
            <Text style={styles.loadMoreText}>Load More</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderEMIInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>What is Home Loan EMI Calculator?</Text>
      <Text style={styles.infoText}>
        A Home Loan EMI Calculator is a financial tool that helps you calculate the Equated Monthly Installment (EMI) for your home loan. It considers factors like loan amount, interest rate, and loan tenure to provide accurate EMI calculations and total cost breakdown for your home purchase.
      </Text>

      <Text style={styles.infoSubtitle}>Key Features</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Calculate monthly EMI for home loans</Text>
        <Text style={styles.bulletPoint}>• Compare different loan tenures</Text>
        <Text style={styles.bulletPoint}>• Visual breakdown of principal vs interest</Text>
        <Text style={styles.bulletPoint}>• Year-wise amortization schedule</Text>
        <Text style={styles.bulletPoint}>• Loan affordability assessment</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use Home Loan EMI Calculator?</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}>1. Enter your desired home loan amount</Text>
        <Text style={styles.stepText}>2. Set the interest rate offered by your bank</Text>
        <Text style={styles.stepText}>3. Choose your preferred loan tenure</Text>
        <Text style={styles.stepText}>4. View your monthly EMI and total cost breakdown</Text>
        <Text style={styles.stepText}>5. Check loan affordability against your income</Text>
      </View>

      <Text style={styles.infoSubtitle}>EMI Calculation Formula</Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>EMI = P × r × (1 + r)^n / ((1 + r)^n - 1)</Text>
        <Text style={styles.formulaText}>Where:</Text>
        <Text style={styles.formulaText}>P = Principal loan amount</Text>
        <Text style={styles.formulaText}>r = Monthly interest rate</Text>
        <Text style={styles.formulaText}>n = Number of monthly installments</Text>
      </View>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to Home Loan EMI Planning & Optimization</Text>
      
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Understanding Home Loan EMI Structure</Text>
        <Text style={styles.seoText}>
          Home loan EMI (Equated Monthly Installment) is the cornerstone of your property purchase journey. Unlike other loans, home loans typically offer the lowest interest rates due to property collateral, making them the most affordable borrowing option for real estate investments. Our advanced calculator helps you understand the complete cost structure including principal repayment, interest charges, and the long-term financial commitment involved in homeownership.
        </Text>
        
        <Text style={styles.seoText}>
          The EMI structure for home loans is designed to be affordable over extended periods, typically ranging from 5 to 30 years. Understanding each component helps you make informed decisions about loan amount, tenure, and prepayment strategies to minimize your total interest burden and achieve faster loan closure.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Key Calculator Features for Homebuyers</Text>
        <View style={styles.featureGrid}>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Comprehensive EMI Analysis</Text>
            <Text style={styles.featureDesc}>Calculate monthly EMI with detailed breakdown of principal vs interest components</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Affordability Assessment</Text>
            <Text style={styles.featureDesc}>Income-to-EMI ratio analysis to determine comfortable loan amount</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Amortization Schedule</Text>
            <Text style={styles.featureDesc}>Year-wise breakdown showing loan balance progression and interest savings</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>• Visual Representation</Text>
            <Text style={styles.featureDesc}>Interactive charts showing total cost distribution and payment timeline</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Home Loan vs Other Property Financing Options</Text>
        <View style={styles.comparisonTable}>
          <View style={styles.comparisonHeader}>
            <Text style={styles.comparisonHeaderText}>Loan Type</Text>
            <Text style={styles.comparisonHeaderText}>Interest Rate</Text>
            <Text style={styles.comparisonHeaderText}>Tenure</Text>
            <Text style={styles.comparisonHeaderText}>Best For</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Home Loan</Text>
            <Text style={styles.comparisonCell}>6.5% - 9.5%</Text>
            <Text style={styles.comparisonCell}>Up to 30 years</Text>
            <Text style={styles.comparisonCell}>Primary residence</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Loan Against Property</Text>
            <Text style={styles.comparisonCell}>8% - 12%</Text>
            <Text style={styles.comparisonCell}>Up to 15 years</Text>
            <Text style={styles.comparisonCell}>Business funding</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Construction Loan</Text>
            <Text style={styles.comparisonCell}>7% - 10%</Text>
            <Text style={styles.comparisonCell}>Up to 25 years</Text>
            <Text style={styles.comparisonCell}>Building new home</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Plot Purchase Loan</Text>
            <Text style={styles.comparisonCell}>8% - 11%</Text>
            <Text style={styles.comparisonCell}>Up to 15 years</Text>
            <Text style={styles.comparisonCell}>Land acquisition</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Step-by-Step Home Loan EMI Calculation</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>1</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Determine Property Budget</Text>
              <Text style={styles.stepDesc}>Assess your property requirements, location preferences, and total budget including registration costs, stamp duty, and other charges.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>2</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Calculate Down Payment</Text>
              <Text style={styles.stepDesc}>Most lenders finance 80-90% of property value. Plan for 10-20% down payment plus additional costs for documentation and processing.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>3</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Compare Interest Rates</Text>
              <Text style={styles.stepDesc}>Research rates from multiple banks, considering fixed vs floating rates, and special offers for different customer segments.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>4</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Optimize Loan Tenure</Text>
              <Text style={styles.stepDesc}>Use our calculator to find the perfect balance between affordable EMI and total interest cost for different tenure options.</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>5</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Plan Prepayment Strategy</Text>
              <Text style={styles.stepDesc}>Analyze the impact of partial prepayments and tenure reduction on total interest savings and loan closure timeline.</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Smart Home Loan Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>🏠 Property Value Appreciation</Text>
            <Text style={styles.strategyDesc}>Choose properties in developing areas with good infrastructure prospects. Property appreciation can significantly offset your EMI burden over time.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>💰 Prepayment Planning</Text>
            <Text style={styles.strategyDesc}>Use bonuses, increments, or windfalls for partial prepayments. Even small prepayments in early years can save lakhs in interest.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>📊 Rate Negotiation</Text>
            <Text style={styles.strategyDesc}>Regularly review and negotiate interest rates with your lender. Good payment history gives you leverage for rate reductions.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>🎯 Tax Benefits Optimization</Text>
            <Text style={styles.strategyDesc}>Maximize deductions under Section 80C (principal) and Section 24 (interest) to reduce your effective EMI burden through tax savings.</Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>⚖️ Balance Transfer Benefits</Text>
            <Text style={styles.strategyDesc}>Consider balance transfer to lenders offering lower rates, but factor in processing fees and other charges before switching.</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Legal & Documentation Requirements</Text>
        <Text style={styles.seoText}>
          Home loan approval requires comprehensive documentation including income proof, property papers, identity verification, and legal clearances. Ensure all property documents are verified by legal experts and the property has clear title. Most lenders also require property insurance and may mandate life insurance coverage for the loan amount.
        </Text>
        
        <Text style={styles.seoText}>
          Key documents include: Salary certificates, IT returns, bank statements, property agreement, title deeds, NOC from builder/society, property tax receipts, and approved building plans. Having complete documentation ready can expedite loan approval and disbursement.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Common Home Loan Mistakes to Avoid</Text>
        <View style={styles.mistakesList}>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Ignoring Hidden Costs</Text>
            <Text style={styles.mistakeDesc}>Not accounting for processing fees, legal charges, stamp duty, registration costs, and maintenance expenses in total budget planning.</Text>
          </View>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Choosing Longest Tenure Always</Text>
            <Text style={styles.mistakeDesc}>Opting for maximum tenure without considering total interest cost. Sometimes shorter tenure with slightly higher EMI saves significant money.</Text>
          </View>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Neglecting Property Verification</Text>
            <Text style={styles.mistakeDesc}>Insufficient due diligence on property legalities, builder reputation, and project approvals leading to future complications.</Text>
          </View>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Inadequate Insurance Coverage</Text>
            <Text style={styles.mistakeDesc}>Underestimating the importance of property and life insurance, leaving family vulnerable to financial risks.</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Success Stories: Smart Home Loan Management</Text>
        <Text style={styles.seoText}>
          <Text style={styles.seoHighlight}>Case Study 1:</Text> Rajesh from Mumbai reduced his 20-year loan to 12 years by making annual prepayments of ₹2 lakhs, saving over ₹18 lakhs in interest while building faster equity in his property.
        </Text>
        
        <Text style={styles.seoText}>
          <Text style={styles.seoHighlight}>Case Study 2:</Text> Priya from Bangalore negotiated a 0.5% rate reduction after 3 years of timely payments, saving ₹3.2 lakhs over the remaining loan tenure on her ₹50 lakh home loan.
        </Text>
        
        <Text style={styles.seoText}>
          <Text style={styles.seoHighlight}>Case Study 3:</Text> The Kumar family optimized their tax planning by timing their home purchase to maximize deductions, effectively reducing their annual tax liability by ₹1.5 lakhs while building their dream home.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>FAQ</Text>
        <Ionicons name={showFAQ ? "chevron-up" : "chevron-down"} size={24} color={colors.textSecondary} />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the maximum home loan tenure?</Text>
            <Text style={styles.faqAnswer}>
              Most banks offer home loans for up to 30 years (360 months). However, the actual tenure depends on your age, income, and bank policies.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How is home loan interest calculated?</Text>
            <Text style={styles.faqAnswer}>
              Home loan interest is typically calculated on a reducing balance method, where interest is charged only on the outstanding principal amount.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is a good EMI to income ratio?</Text>
            <Text style={styles.faqAnswer}>
              Financial experts recommend keeping your EMI within 40% of your monthly income to maintain a healthy financial balance.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I prepay my home loan?</Text>
            <Text style={styles.faqAnswer}>
              Yes, most banks allow prepayment of home loans. Prepayment can significantly reduce your total interest burden and loan tenure.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsContainer}>
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Monthly EMI</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.emi)}</Text>
        </View>
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Principal amount</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.principal)}</Text>
        </View>
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total interest</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.totalInterest)}</Text>
        </View>
        <View style={[styles.resultItem, { borderBottomWidth: 0 }]}>
          <Text style={styles.resultLabel}>Total amount</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.totalAmount)}</Text>
        </View>
      </View>
    );
  };

  const renderDonutChart = () => {
    if (!results) return null;

    const { principal, totalInterest } = results;
    const total = principal + totalInterest;
    const principalPercentage = (principal / total) * 100;
    const interestPercentage = (totalInterest / total) * 100;

    const radius = 80;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;
    const principalStroke = (principalPercentage / 100) * circumference;
    const interestStroke = (interestPercentage / 100) * circumference;

    return (
      <View style={styles.chartContainer}>
        <Svg width={200} height={200}>
          <Circle
            cx={100}
            cy={100}
            r={radius}
            stroke={colors.border}
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <Circle
            cx={100}
            cy={100}
            r={radius}
            stroke={colors.primary}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${principalStroke} ${circumference}`}
            strokeDashoffset={0}
            transform={`rotate(-90 100 100)`}
          />
          <Circle
            cx={100}
            cy={100}
            r={radius}
            stroke={colors.success}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${interestStroke} ${circumference}`}
            strokeDashoffset={-principalStroke}
            transform={`rotate(-90 100 100)`}
          />
          <SvgText
            x={100}
            y={95}
            textAnchor="middle"
            fontSize={16}
            fontWeight="600"
            fill={colors.text}
          >
            Total Amount
          </SvgText>
          <SvgText
            x={100}
            y={115}
            textAnchor="middle"
            fontSize={14}
            fill={colors.textSecondary}
          >
            ₹{formatNumber(results.totalAmount)}
          </SvgText>
        </Svg>
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Loan</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Interest</Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView 
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {renderHeader()}
        
        <View style={styles.mainContent}>
          <View style={styles.leftPanel}>
            {renderCalculator()}
          </View>
          
          <View style={styles.rightPanel}>
            {renderDonutChart()}
            {renderResults()}
          </View>
        </View>

        {renderAmortizationSchedule()}
        {renderEMIInformation()}
        {renderSEOContent()}
        {renderFAQ()}
        <Footer />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    backgroundColor: colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 40,
    alignItems: 'center',
  },
  headerContent: {
    maxWidth: 800,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    marginBottom: 10,
  },
  headerSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: 24,
  },
  content: {
    flexDirection: isMobile ? 'column' : 'row',
    width: '100%',
    alignSelf: 'stretch',
    padding: 20,
    gap: 30,
  },
  mainContent: {
    flexDirection: (isWeb && !isMobile) ? 'row' : 'column',
    minHeight: (isWeb && !isMobile) ? 600 : 'auto',
  },
  fullWidthSection: {
    flex: 1,
    alignSelf: 'stretch',
  },
  leftPanel: {
    flex: (isWeb && !isMobile) ? 1 : 1,
    backgroundColor: colors.background,
    padding: 20,
  },
  rightPanel: {
    width: (isWeb && !isMobile) ? 400 : '100%',
    backgroundColor: colors.surface,
    borderLeftWidth: (isWeb && !isMobile) ? 1 : 0,
    borderLeftColor: colors.border,
    padding: 20,
  },
  calculatorContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 20,
  },
  inputGroup: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.surface,
  },
  currencySymbol: {
    fontSize: 16,
    color: colors.textSecondary,
    marginRight: 8,
  },
  textInput: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: colors.text,
  },
  presetContainer: {
    marginBottom: 16,
  },
  presetLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textSecondary,
    marginBottom: 8,
  },
  presetButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  presetButton: {
    flex: 1,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  presetButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
  },
  affordabilityContainer: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 20,
  },
  affordabilityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  affordabilityTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  affordabilityContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  affordabilityResults: {
    backgroundColor: colors.background,
    borderRadius: 6,
    padding: 12,
    marginTop: 12,
  },
  affordabilityItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  affordabilityLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  affordabilityValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  affordabilityNote: {
    fontSize: 12,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  sliderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  valueContainer: {
    backgroundColor: colors.success,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  valueText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  slider: {
    width: '100%',
    height: 40,
  },
  sliderThumb: {
    backgroundColor: colors.success,
    width: 20,
    height: 20,
  },
  chartContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  chartLegend: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resultsContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 20,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  resultItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  resultLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resultValue: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  scheduleContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    marginVertical: 20,
    width: '100%',
    alignSelf: 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  scheduleTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 20,
    textAlign: 'left',
  },
  scheduleTable: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  tableHeaderText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'left',
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableCellText: {
    flex: 1,
    fontSize: 12,
    color: colors.textSecondary,
    textAlign: 'left',
    paddingHorizontal: 4,
  },
  loadMoreButton: {
    backgroundColor: colors.success,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'center',
    marginTop: 20,
  },
  loadMoreText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  faqContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    marginVertical: 20,
    width: '100%',
    alignSelf: 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'left',
  },
  faqContent: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  faqItem: {
    marginBottom: 20,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  infoContainer: {
    marginVertical: 20,
    padding: 20,
    backgroundColor: colors.surface,
    borderRadius: 12,
    width: '100%',
    alignSelf: 'stretch',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  infoTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 16,
    textAlign: 'left',
  },
  infoSubtitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginTop: 20,
    marginBottom: 12,
    textAlign: 'left',
  },
  infoText: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 12,
    textAlign: 'left',
  },
  bulletList: {
    marginLeft: 16,
    marginBottom: 16,
  },
  bulletPoint: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 4,
    textAlign: 'left',
  },
  formulaBox: {
    backgroundColor: colors.background,
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    marginVertical: 12,
  },
  formulaText: {
    fontSize: 16,
    fontFamily: 'monospace',
    color: colors.text,
    marginBottom: 4,
    textAlign: 'left',
  },
  stepsList: {
    marginLeft: 8,
    marginBottom: 16,
  },
  stepText: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 8,
    textAlign: 'left',
  },
  // SEO Content Styles
  seoContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    marginVertical: 20,
    width: '100%',
    alignSelf: 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  seoTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  seoSection: {
    marginBottom: 24,
  },
  seoSubtitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 12,
    textAlign: 'left',
  },
  seoText: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 12,
    textAlign: 'left',
  },
  seoHighlight: {
    fontWeight: '600',
    color: colors.primary,
  },
  featureGrid: {
    gap: 16,
  },
  featureItem: {
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  featureDesc: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  comparisonTable: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
  },
  comparisonHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  comparisonHeaderText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  comparisonRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.surface,
  },
  comparisonCell: {
    flex: 1,
    fontSize: 14,
    color: colors.text,
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  stepsList: {
    gap: 16,
  },
  stepItem: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: 32,
    height: 32,
    backgroundColor: colors.primary,
    borderRadius: 16,
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 32,
    marginRight: 16,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  stepDesc: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  strategiesList: {
    gap: 16,
  },
  strategyItem: {
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: colors.success,
  },
  strategyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  strategyDesc: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  mistakesList: {
    gap: 16,
  },
  mistakeItem: {
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
  },
  mistakeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  mistakeDesc: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
});

export default HomeLoanEMICalculatorScreen;